import type * as Papa from 'papaparse';

// Kept free of Angular and the DOM so the parsing web worker can import it.

const CANDIDATE_DELIMITERS = [',', ';', '\t', '|'] as const;
type Delimiter = (typeof CANDIDATE_DELIMITERS)[number];

/** Structurally a `TableColumn`, declared here so the worker does not import the table component tree. */
export interface CsvColumn<T> {
  key: keyof T;
  label: string;
  type: 'string' | 'number' | 'boolean';
  sortable: boolean;
}

export interface CsvParseResult<T = Record<string, unknown>> {
  data: T[];
  columns: CsvColumn<T>[];
  errors: string[];
}

export interface CsvParseWorkerRequest {
  file: File;
}

export type CsvParseWorkerResponse =
  | { ok: true; result: CsvParseResult }
  | { ok: false; message: string };

export interface CsvParseOptions {
  header?: boolean;
  dynamicTyping?: boolean;
  skipEmptyLines?: boolean | 'greedy';
  preview?: number;
  transformHeader?(header: string, index: number): string;
}

/**
 * Detects the delimiter of a CSV file by analyzing the first line (headers).
 * Falls back to ',' if no delimiter can be determined.
 */
export function detectDelimiter(csvContent: string): Delimiter {
  const firstLine = csvContent.slice(0, csvContent.indexOf('\n')).trim();

  if (!firstLine) return ',';

  const counts = CANDIDATE_DELIMITERS.map((delimiter) => ({
    delimiter,
    count: firstLine.split(delimiter).length - 1
  }));

  const best = counts.reduce((a, b) => (b.count > a.count ? b : a));

  return best.count > 0 ? best.delimiter : ',';
}

export const CSV_PARSE_DEFAULTS = {
  delimiter: (csvContent: string) => detectDelimiter(csvContent),
  header: true,
  dynamicTyping: false,
  // Spreadsheets export a blank row as `,,,`, which `true` keeps as a row of empty strings.
  skipEmptyLines: 'greedy',
  transformHeader: (header: string) => header.trim()
} as const;

export function toCsvParseResult<T>(results: Papa.ParseResult<T>): CsvParseResult<T> {
  const data = (results.data as T[]).map((row) => {
    if (row && typeof row === 'object' && '__parsed_extra' in row) {
      delete (row as Record<string, unknown>)['__parsed_extra'];
    }
    return row;
  });
  const errors = results.errors.map((err: Papa.ParseError) => err.message);
  const columns = generateColumns<T>(data);

  return {
    data,
    columns,
    errors
  };
}

function generateColumns<T>(data: T[]): CsvColumn<T>[] {
  if (!data || data.length === 0) {
    return [];
  }

  const firstRow = data[0];
  if (!firstRow || typeof firstRow !== 'object') {
    return [];
  }

  return Object.keys(firstRow).map((key) => {
    const sampleValue = firstRow[key as keyof T];
    const columnType = inferColumnType(sampleValue);

    return {
      key: key as keyof T,
      label: key,
      type: columnType,
      sortable: true
    };
  });
}

function inferColumnType(value: unknown): 'string' | 'number' | 'boolean' {
  if (typeof value === 'number') {
    return 'number';
  }
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  return 'string';
}
